import React, { useState, useEffect } from 'react';
import { WeatherService } from '../services/weatherService';
import { GeolocationService } from '../services/geolocationService';
import { WeatherData } from '../types/weather.types';

// Madison, WI coordinates default
const MADISON_COORDS = {
    latitude: 43.0731,
    longitude: -89.4012,
};

const MinimalWeather: React.FC = () => {
    const [weather, setWeather] = useState<WeatherData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);
    const [locationName, setLocationName] = useState<string>("Madison, WI");

    const fetchWeather = async (lat?: number, lon?: number, name?: string) => {
        setLoading(true);
        setError(null);
        try {
            const finalLat = lat ?? MADISON_COORDS.latitude;
            const finalLon = lon ?? MADISON_COORDS.longitude;

            const data = await WeatherService.fetchCurrentWeather(finalLat, finalLon, true);
            setWeather(data);
            setLastUpdated(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
            if (name) setLocationName(name);
        } catch (err: unknown) {
            console.error(err);
            setError("Failed to fetch weather data.");
        } finally {
            setLoading(false);
        }
    };

    const handleUseCurrentLocation = async () => {
        setLoading(true);
        try {
            const result = await GeolocationService.getLocation();
            const { city, region, countryName, latitude, longitude } = result.location;

            // Construct a descriptive name: City, Region, Country (filtering out 'Unknown' values)
            const parts = [city, region, countryName].filter(p => p && p !== 'Unknown');
            const name = parts.length > 0 ? parts.join(', ') : "Detected Location";

            await fetchWeather(latitude, longitude, name);
        } catch (err) {
            console.error(err);
            setError("Could not determine your location.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWeather();
    }, []);

    return (
        <div className="space-y-12">
            <header className="flex justify-between items-baseline flex-wrap gap-4">
                <div>
                    <h1 className="text-xl font-bold uppercase tracking-widest">/weather</h1>
                    <p className="mt-2 text-sm text-gray-600 tracking-tight">
                        Environmental conditions in <span className="text-black font-medium">{locationName}</span>.
                    </p>
                </div>
                <div className="flex gap-4">
                    <button
                        onClick={handleUseCurrentLocation}
                        className="text-[10px] font-mono uppercase underline hover:no-underline transition-all"
                        disabled={loading}
                    >
                        {loading ? '[ locating ]' : '[ use current location ]'}
                    </button>
                    <button
                        onClick={() => fetchWeather(MADISON_COORDS.latitude, MADISON_COORDS.longitude, "Madison, WI")}
                        className="text-[10px] font-mono uppercase underline hover:no-underline transition-all"
                        disabled={loading}
                    >
                        [ reset ]
                    </button>
                </div>
            </header>

            {error && (
                <div className="border border-black p-6 bg-red-50 text-xs text-red-600 font-mono">
                    System Error: {error}
                </div>
            )}

            {loading && !weather && (
                <div className="text-xs font-mono animate-pulse">Establishing connection to weather data...</div>
            )}

            {weather && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    {[
                        { label: "Temperature", value: `${weather.temperature.toFixed(1)}°C` },
                        { label: "Feels Like", value: `${weather.feelsLike.toFixed(1)}°C` },
                        { label: "Humidity", value: `${weather.humidity}%` },
                        { label: "Pressure", value: `${weather.pressure} hPa` },
                        { label: "Wind Speed", value: `${weather.windSpeed} km/h` },
                        { label: "Visibility", value: `${weather.visibility.toFixed(1)} km` },
                        { label: "Snowfall", value: weather.snowfall > 0 ? `${weather.snowfall.toFixed(1)} cm` : 'None' },
                        { label: "Precipitation", value: weather.precipitation > 0 ? `${weather.precipitation.toFixed(1)} mm` : 'None' },
                        { label: "UV Index", value: weather.uvIndex.toFixed(1) },
                        { label: "Conditions", value: weather.isRaining ? 'Rain' : weather.isSnowing ? 'Snow' : 'Clear' }
                    ].map((item, i) => (
                        <div key={i} className="group">
                            <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1 group-hover:text-black transition-colors">{item.label}</span>
                            <span className="text-lg font-medium tabular-nums">{item.value}</span>
                        </div>
                    ))}
                </div>
            )}

            {lastUpdated && !loading && (
                <div className="pt-8 border-t border-black/5 text-[10px] text-gray-400 font-mono uppercase tracking-widest">
                    Last update verified: {lastUpdated}
                </div>
            )}
        </div>
    );
};

export default MinimalWeather;
